import { Outlet, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Compass, Flame, MessageCircle, Bell, User2, Shield } from "lucide-react";
import { Logo } from "@/components/Logo";
import { NotificationPrompt } from "@/components/NotificationPrompt";
import { ExpiryBanner } from "@/components/ExpiryBanner";
import { UsagePill } from "@/components/UsagePill";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const items = [
  { to: "/discover", label: "Discover", icon: Compass, testid: "nav-discover" },
  { to: "/matches", label: "Matches", icon: Flame, testid: "nav-matches" },
  { to: "/messages", label: "Messages", icon: MessageCircle, testid: "nav-messages" },
  { to: "/activity", label: "Activity", icon: Bell, testid: "nav-activity" },
  { to: "/profile", label: "Profile", icon: User2, testid: "nav-profile" },
];

export function AppLayout() {
  const { user } = useAuth();
  const location = useLocation();
  const [badges, setBadges] = useState({ activity: 0, messages: 0 });

  useEffect(() => {
    let active = true;
    const poll = async () => {
      api.post("/presence/ping").catch(() => {});
      try {
        const [n, c] = await Promise.all([
          api.get("/notifications/unread-count"),
          api.get("/conversations"),
        ]);
        if (!active) return;
        const unreadMsgs = (c.data || []).reduce((a, x) => a + (x.unread || 0), 0);
        setBadges({ activity: n.data.count || 0, messages: unreadMsgs });
      } catch {}
    };
    poll();
    const id = setInterval(poll, 5000);
    return () => { active = false; clearInterval(id); };
  }, [location.pathname]);

  return (
    <div className="min-h-screen pb-24">
      <header className="sticky top-0 z-40 glass border-b border-amber-500/10">
        <div className="max-w-5xl mx-auto px-5 h-16 flex items-center justify-between">
          <Logo size="text-2xl" to="/discover" />
          <div className="flex items-center gap-3">
            <UsagePill />
            {user?.role === "admin" && (
              <NavLink to="/admin" data-testid="nav-admin" className="text-amber-400 hover:text-amber-300 transition-colors">
                <Shield className="h-5 w-5" />
              </NavLink>
            )}
            <NavLink to="/profile" className="h-9 w-9 rounded-full overflow-hidden ring-2 ring-amber-500/40 bg-emerald-900/40 flex items-center justify-center">
              {user?.photos?.[0] || user?.picture ? (
                <img src={require("@/lib/api").imgUrl(user.photos?.[0] || user.picture)} alt="me" className="h-full w-full object-cover" />
              ) : (
                <User2 className="h-4 w-4 text-amber-300" />
              )}
            </NavLink>
          </div>
        </div>
      </header>

      <ExpiryBanner />

      <NotificationPrompt />

      <main className="max-w-5xl mx-auto px-5 py-6">
        <Outlet />
      </main>

      <nav className="fixed bottom-0 inset-x-0 z-40">
        <div className="max-w-5xl mx-auto px-4 pb-4">
          <div className="glass rounded-[22px] border border-amber-500/20 shadow-2xl px-2 py-2 flex items-center justify-between">
            {items.map(({ to, label, icon: Icon, testid }) => {
              const badge = to === "/activity" ? badges.activity : to === "/messages" ? badges.messages : 0;
              return (
                <NavLink key={to} to={to} data-testid={testid}
                  className={({ isActive }) =>
                    `relative flex-1 flex flex-col items-center gap-1 py-2 rounded-2xl transition-all ${
                      isActive ? "text-amber-300" : "text-emerald-100/50 hover:text-emerald-100/80"
                    }`
                  }>
                  {({ isActive }) => (
                    <>
                      {isActive && <span className="absolute inset-0 rounded-2xl bg-amber-400/10 border border-amber-400/20" />}
                      <span className="relative">
                        <Icon className="h-5 w-5" />
                        {badge > 0 && (
                          <span className="absolute -top-2 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-pink-500 text-[10px] font-bold text-white flex items-center justify-center">
                            {badge > 9 ? "9+" : badge}
                          </span>
                        )}
                      </span>
                      <span className="relative text-[10px] font-semibold tracking-wide">{label}</span>
                    </>
                  )}
                </NavLink>
              );
            })}
          </div>
        </div>
      </nav>
    </div>
  );
}
